// src/components/EmployeeHolidays.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import "./EmployeeHolidays.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const EmployeeHolidays = () => {
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const employeeLocation = storedUser.location || "";
  const socketRef = useRef(null);

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const fetchHolidays = useCallback(() => {
    let url = `${API_URL}/api/employee_holidays`;
    if (employeeLocation) url += `?location=${employeeLocation}`;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Error fetching holidays");
        return res.json();
      })
      .then((data) => {
        setHolidays(data.filter((h) => h.approval_status === "Approved"));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [employeeLocation]);

  useEffect(() => {
    if (!employeeLocation) {
      setLoading(false);
      return;
    }

    socketRef.current = io(API_URL);
    socketRef.current.emit("join", { userId: storedUser.id });
    socketRef.current.on("holidaysChanged", fetchHolidays);
    fetchHolidays();

    return () => {
      socketRef.current.off("holidaysChanged", fetchHolidays);
      socketRef.current.disconnect();
    };
  }, [fetchHolidays, storedUser.id, employeeLocation]);

  const upcomingHolidays = holidays.filter((h) => {
    const d = new Date(h.holiday_date);
    d.setHours(0, 0, 0, 0);
    return d >= today;
  });

  const pastHolidays = holidays.filter((h) => {
    const d = new Date(h.holiday_date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      day: d.toLocaleDateString("en-US", { weekday: "short" }),
      date: d.getDate(),
      month: d.toLocaleDateString("en-US", { month: "short" }),
      year: d.getFullYear(),
      full: d.toLocaleDateString("en-CA"),
    };
  };

  const getMonthColor = (dateStr) => {
    const month = new Date(dateStr).getMonth();
    const colors = [
      "#4f8ef7", "#7b6cf6", "#e85d75", "#f4a261",
      "#2ec4b6", "#e9c46a", "#f77f00", "#06d6a0",
      "#118ab2", "#ef476f", "#8338ec", "#3a86ff",
    ];
    return colors[month];
  };

  if (!employeeLocation) {
    return (
      <div className="hol-wrapper">
        <div className="hol-card">
          <div className="hol-header">
            <span className="hol-icon">🗓</span>
            <h2 className="hol-title">Holiday Calendar</h2>
          </div>
          <div className="hol-empty-state">
            <div className="hol-empty-icon">📍</div>
            <p className="hol-empty-text">No location set</p>
            <p className="hol-empty-sub">Update your profile with your location to view upcoming holidays.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="hol-wrapper">
        <div className="hol-card">
          <div className="hol-header">
            <span className="hol-icon">🗓</span>
            <h2 className="hol-title">Holiday Calendar</h2>
          </div>
          <div className="hol-loading">
            {[1, 2, 3].map((i) => (
              <div key={i} className="hol-skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const renderHolidayRow = (holiday, idx, isPast) => {
    const fmt = formatDate(holiday.holiday_date);
    const accentColor = getMonthColor(holiday.holiday_date);

    return (
      <div
        key={holiday.id}
        className={`hol-row ${isPast ? "hol-row--past" : "hol-row--upcoming"}`}
        style={{ "--accent": accentColor, animationDelay: `${idx * 60}ms` }}
      >
        <div className="hol-row__index">{idx + 1}</div>
        <div className="hol-row__date-block" style={{ background: `${accentColor}18`, borderColor: `${accentColor}40` }}>
          <span className="hol-row__day">{fmt.day}</span>
          <span className="hol-row__num" style={{ color: accentColor }}>{fmt.date}</span>
          <span className="hol-row__month">{fmt.month}</span>
        </div>
        <div className="hol-row__info">
          <p className="hol-row__name">{holiday.holiday_name}</p>
          <p className="hol-row__year">{fmt.year}</p>
        </div>
        {!isPast && (
          <div className="hol-row__badge" style={{ background: `${accentColor}18`, color: accentColor }}>
            Upcoming
          </div>
        )}
        {isPast && (
          <div className="hol-row__badge hol-row__badge--past">
            Past
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="hol-wrapper">
      <div className="hol-card">
        <div className="hol-header">
          <div className="hol-header__left">
            <span className="hol-icon">🗓</span>
            <div>
              <h2 className="hol-title">Holiday Calendar</h2>
              <p className="hol-subtitle">{employeeLocation} &bull; {holidays.length} holidays</p>
            </div>
          </div>
          <div className="hol-stats">
            <div className="hol-stat">
              <span className="hol-stat__num">{upcomingHolidays.length}</span>
              <span className="hol-stat__label">Upcoming</span>
            </div>
            <div className="hol-stat hol-stat--muted">
              <span className="hol-stat__num">{pastHolidays.length}</span>
              <span className="hol-stat__label">Past</span>
            </div>
          </div>
        </div>

        <div className="hol-body">
          {holidays.length === 0 ? (
            <div className="hol-empty-state">
              <div className="hol-empty-icon">🏖️</div>
              <p className="hol-empty-text">No holidays found</p>
              <p className="hol-empty-sub">No approved holidays are available for your location yet.</p>
            </div>
          ) : (
            <>
              {upcomingHolidays.length > 0 && (
                <section className="hol-section">
                  <h3 className="hol-section__title">
                    <span className="hol-section__dot hol-section__dot--green" />
                    Upcoming
                  </h3>
                  <div className="hol-list">
                    {upcomingHolidays.map((h, i) => renderHolidayRow(h, i + 1, false))}
                  </div>
                </section>
              )}

              {pastHolidays.length > 0 && (
                <section className="hol-section">
                  <h3 className="hol-section__title">
                    <span className="hol-section__dot" />
                    Past
                  </h3>
                  <div className="hol-list">
                    {pastHolidays.map((h, i) => renderHolidayRow(h, i + 1, true))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeHolidays;